import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Dropdown } from "react-bootstrap";
import { useDispatch } from "react-redux";

import ConfirmModal from "../confirmModal/confirmModel";
import { clearAuthSession } from "../../helpers/authSession";
import useMyEmpref from "../../hooks/useMyEmpref";
import Constant from "../../util/constant";
import { showToast } from "../../store/features/toast/toastSlice";
import { closeMobileSidebarDrawer } from "../../util/sidebarToggle";
import "./profileHeaderDropdown.css";

function ProfileHeaderDropdown({ onBeforeLogoutOpen }) {
  const name = window.localStorage.getItem(
    Constant.SESSIONSTORAGEKEYS.USER_NAME
  );
  const role = window.localStorage.getItem("role");
  const isClubEmployee = role === Constant.ROLES.CLUB_EMPLOYEE;
  const { empref: myEmpref } = useMyEmpref();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [logoutModal, setLogoutModal] = useState(false);
  const [copyingEmpref, setCopyingEmpref] = useState(false);

  const copyEmpref = useCallback(async () => {
    const value = String(myEmpref || "").trim();
    if (!value || copyingEmpref) return;
    setCopyingEmpref(true);
    try {
      await navigator.clipboard.writeText(value);
      dispatch(
        showToast({
          message: t("CLUB_EMPLOYEE_EMPREF_COPIED"),
          type: "success",
        })
      );
    } catch {
      dispatch(
        showToast({
          message: t("CLUB_EMPLOYEE_EMPREF_COPY_FAILED"),
          type: "error",
        })
      );
    } finally {
      setCopyingEmpref(false);
    }
  }, [copyingEmpref, dispatch, myEmpref, t]);

  const closeLogoutModel = () => {
    setLogoutModal(false);
  };

  const logoutFunction = async () => {
    setLogoutModal(false);
    await clearAuthSession(dispatch);
    navigate("/", { replace: true });
  };

  const logoutModalOpen = () => {
    if (typeof onBeforeLogoutOpen === "function") {
      onBeforeLogoutOpen();
    } else {
      closeMobileSidebarDrawer();
    }
    setLogoutModal(true);
  };

  const displayName = String(name || "").trim() || t("ACCOUNT");

  return (
    <>
      <Dropdown className="profile-style">
        <Dropdown.Toggle variant="success" id="profile-header-dropdown">
          <span className="d-xl-inline-block profile-name-style">
            {displayName}
          </span>
          <i className="mdi mdi-chevron-down d-xl-inline-block" />
        </Dropdown.Toggle>

        <Dropdown.Menu align="end">
          {isClubEmployee && myEmpref ? (
            <>
              <div className="profile-menu-empref px-3 py-2">
                <span className="profile-menu-empref-label d-block">
                  {t("CLUB_EMPLOYEE_EMPREF")}
                </span>
                <div className="profile-menu-empref-row d-flex align-items-center gap-2">
                  <span className="profile-menu-empref-value fw-semibold">
                    {myEmpref}
                  </span>
                  <button
                    type="button"
                    className="profile-menu-empref-copy"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      copyEmpref();
                    }}
                    disabled={copyingEmpref}
                    title={t("CLUB_EMPLOYEE_EMPREF_COPY")}
                    aria-label={t("CLUB_EMPLOYEE_EMPREF_COPY")}
                  >
                    <i className="bx bx-copy" aria-hidden />
                  </button>
                </div>
              </div>
              <div className="lineBorderStyle" />
            </>
          ) : null}
          <Dropdown.Item to="/change-password" as={Link}>
            <i className="bx bx-lock-open font-size-24 align-middle" />
            <span>{t("CHANGE_PASSWORD")}</span>
          </Dropdown.Item>
          <Dropdown.Item onClick={logoutModalOpen}>
            <i className="bx bx-log-out font-size-24 align-middle text-danger" />
            <span>{t("LOGOUT")}</span>
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>

      <ConfirmModal
        modalTitle={t("LOGOUT")}
        modalDescription={t("ARE_YOU_SURE_YOU_WANT_TO_LOGOUT_OF_THIS_APP")}
        isUnderline={false}
        showModal={logoutModal}
        leftBtnTitle={t("YES_LOGOUT")}
        isModelIcon={true}
        iconColor={"mdi mdi-exit-to-app "}
        iconCricle={"iconCricleDelete"}
        leftBtnClass={"ok_Btn"}
        isFirstButton={true}
        isSecondButton={true}
        rightBtnClass="borderButton"
        rightBtnTitle={t("NO_CANCEL")}
        onProcced={logoutFunction}
        rightButtonClick={closeLogoutModel}
      />
    </>
  );
}

ProfileHeaderDropdown.propTypes = {
  onBeforeLogoutOpen: PropTypes.func,
};

export default ProfileHeaderDropdown;
